import { useState } from 'react';
import { useWorkflowAutomation } from '@/hooks/useWorkflowAutomation';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Play, 
  Pause, 
  Plus, 
  Settings, 
  Zap, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Activity,
  GitBranch
} from 'lucide-react';

export function AdvancedWorkflowManager() {
  const { 
    workflowTemplates, 
    workflowExecutions, 
    workflowStats, 
    isLoading, 
    createWorkflow, 
    updateWorkflow,
    deleteWorkflow,
    toggleWorkflowStatus,
    executeWorkflow,
    isCreating 
  } = useWorkflowAutomation();

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState<any>(null);
  const [workflowName, setWorkflowName] = useState('');
  const [workflowDescription, setWorkflowDescription] = useState('');
  const [triggerType, setTriggerType] = useState('bill_uploaded');
  const [triggerConfig, setTriggerConfig] = useState('{}');
  const [actions, setActions] = useState('[{"type": "process_bill", "config": {}}]');
  const [conditions, setConditions] = useState('{}');

  const statusColors = {
    running: 'default',
    completed: 'default',
    failed: 'destructive',
    cancelled: 'secondary'
  } as const;

  const statusIcons = {
    running: Activity,
    completed: CheckCircle,
    failed: XCircle,
    cancelled: Clock
  };

  const triggerTypes = [
    { value: 'bill_uploaded', label: 'Bill Uploaded' },
    { value: 'bill_processed', label: 'Bill Processed' },
    { value: 'supplier_detected', label: 'Supplier Detected' },
    { value: 'schedule', label: 'Scheduled' },
    { value: 'webhook', label: 'Webhook' },
    { value: 'manual', label: 'Manual Trigger' }
  ];

  const handleCreateWorkflow = () => {
    try {
      const parsedTriggerConfig = JSON.parse(triggerConfig);
      const parsedActions = JSON.parse(actions);
      const parsedConditions = JSON.parse(conditions);

      createWorkflow({
        name: workflowName,
        description: workflowDescription,
        trigger_type: triggerType,
        trigger_config: parsedTriggerConfig,
        actions: parsedActions,
        conditions: parsedConditions
      });

      resetForm();
      setShowCreateDialog(false);
    } catch (error) {
      alert('Please enter valid JSON for configuration fields');
    }
  };

  const resetForm = () => {
    setWorkflowName('');
    setWorkflowDescription('');
    setTriggerType('bill_uploaded');
    setTriggerConfig('{}');
    setActions('[{"type": "process_bill", "config": {}}]');
    setConditions('{}');
  };

  const handleExecuteWorkflow = (templateId: string) => {
    executeWorkflow({ templateId });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div className="h-4 bg-muted animate-pulse rounded w-20"></div>
                <div className="h-4 w-4 bg-muted animate-pulse rounded"></div>
              </CardHeader>
              <CardContent>
                <div className="h-7 bg-muted animate-pulse rounded w-8 mb-1"></div>
                <div className="h-3 bg-muted animate-pulse rounded w-16"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Workflow Automation</h2>
          <p className="text-muted-foreground">Create and manage automated workflows for your business processes</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Create Workflow
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create Workflow Template</DialogTitle>
              <DialogDescription>
                Configure an automated workflow with triggers, conditions, and actions.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="workflowName">Workflow Name</Label>
                  <Input
                    id="workflowName"
                    value={workflowName}
                    onChange={(e) => setWorkflowName(e.target.value)}
                    placeholder="My Workflow"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="triggerType">Trigger Type</Label>
                  <Select value={triggerType} onValueChange={setTriggerType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {triggerTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="workflowDescription">Description</Label>
                <Textarea
                  id="workflowDescription"
                  value={workflowDescription}
                  onChange={(e) => setWorkflowDescription(e.target.value)}
                  placeholder="Describe what this workflow does..."
                  rows={2}
                />
              </div>
              <Separator />
              <div className="space-y-2">
                <Label htmlFor="triggerConfig">Trigger Configuration (JSON)</Label>
                <Textarea
                  id="triggerConfig"
                  value={triggerConfig}
                  onChange={(e) => setTriggerConfig(e.target.value)}
                  placeholder={`{"schedule": "0 9 * * *", "filters": {}}`}
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="conditions">Conditions (JSON)</Label>
                <Textarea
                  id="conditions"
                  value={conditions}
                  onChange={(e) => setConditions(e.target.value)}
                  placeholder={`{"amount_threshold": 1000, "supplier_type": "utility"}`}
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="actions">Actions (JSON Array)</Label>
                <Textarea
                  id="actions"
                  value={actions}
                  onChange={(e) => setActions(e.target.value)}
                  placeholder={`[{"type": "send_email", "config": {"to": "admin@company.com"}}, {"type": "update_status", "config": {"status": "processed"}}]`}
                  rows={4}
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateWorkflow} disabled={isCreating}>
                {isCreating ? "Creating..." : "Create Workflow"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Workflow Statistics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Workflows</CardTitle>
            <GitBranch className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{workflowStats.totalTemplates}</div>
            <p className="text-xs text-muted-foreground">configured</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active</CardTitle>
            <Zap className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{workflowStats.activeTemplates}</div>
            <p className="text-xs text-muted-foreground">running workflows</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Executions</CardTitle>
            <Activity className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{workflowStats.totalExecutions}</div>
            <p className="text-xs text-muted-foreground">total runs</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{(workflowStats.avgSuccessRate || 0).toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground">average success</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="templates" className="space-y-4">
        <TabsList>
          <TabsTrigger value="templates">Workflow Templates</TabsTrigger>
          <TabsTrigger value="executions">Recent Executions</TabsTrigger>
        </TabsList>

        <TabsContent value="templates" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Workflow Templates</CardTitle>
              <CardDescription>
                Manage your automated workflow templates
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Trigger</TableHead>
                    <TableHead>Usage</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {workflowTemplates.map((template) => (
                    <TableRow key={template.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{template.name}</div>
                          <div className="text-sm text-muted-foreground">
                            {template.description}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {template.trigger_type.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>{template.usage_count} runs</TableCell>
                      <TableCell>
                        <Switch
                          checked={template.is_active}
                          onCheckedChange={(checked) =>
                            toggleWorkflowStatus({ id: template.id, isActive: checked })
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleExecuteWorkflow(template.id)}
                          >
                            <Play className="mr-1 h-3 w-3" />
                            Run
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deleteWorkflow(template.id)}
                          >
                            Delete
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {workflowTemplates.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        No workflow templates found. Create your first workflow to get started.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="executions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Recent Executions</CardTitle>
              <CardDescription>
                View the latest workflow execution results
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Workflow</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Progress</TableHead>
                    <TableHead>Started</TableHead>
                    <TableHead>Duration</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {workflowExecutions.map((execution) => {
                    const StatusIcon = statusIcons[execution.status as keyof typeof statusIcons];
                    const duration = execution.completed_at 
                      ? Math.round((new Date(execution.completed_at).getTime() - new Date(execution.started_at).getTime()) / 1000)
                      : null;
                    
                    return (
                      <TableRow key={execution.id}>
                        <TableCell>
                          <div className="font-medium">
                            {execution.workflow_templates?.name || 'Unknown Workflow'}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <StatusIcon className="h-4 w-4" />
                            <Badge variant={statusColors[execution.status as keyof typeof statusColors]}>
                              {execution.status}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {execution.steps_completed}/{execution.total_steps} steps
                          </div>
                        </TableCell>
                        <TableCell>
                          {new Date(execution.started_at).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          {duration ? `${duration}s` : 'Running...'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {workflowExecutions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        No workflow executions found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}